import { Box } from '@chakra-ui/react'
import { useEffect, useRef } from 'react'
import * as THREE from 'three'

type Props = {
	emoji?: string
	count?: number
	size?: number
	zIndex?: number
}

const CAMERA_Z = 30
const CAMERA_FOV_DEG = 60

const makeEmojiTexture = (emoji: string, size = 256): THREE.CanvasTexture => {
	const canvas = document.createElement('canvas')
	canvas.width = size
	canvas.height = size
	const ctx = canvas.getContext('2d')
	if (!ctx) return new THREE.CanvasTexture(canvas)
	ctx.clearRect(0, 0, size, size)
	ctx.font = `${size * 0.75}px Arial`
	ctx.textAlign = 'center'
	ctx.textBaseline = 'middle'
	ctx.fillText(emoji, size / 2, size / 2)
	const texture = new THREE.CanvasTexture(canvas)
	texture.colorSpace = THREE.SRGBColorSpace
	return texture
}

type ParticleData = {
	mesh: THREE.Mesh
	radius: number
	vx: number
	vy: number
	vz: number
	rotSpeed: number
	rotAxis: THREE.Vector3
}

export const FlyingEmojis = ({ emoji = '🍸', count = 10, size = 2, zIndex = 0 }: Props) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const countRef = useRef(count)
	const sizeRef = useRef(size)
	countRef.current = count
	sizeRef.current = size

	useEffect(() => {
		const container = containerRef.current
		if (!container) return

		let cancelled = false
		let cleanup: (() => void) | null = null

		const initFrameId = requestAnimationFrame(() => {
			if (cancelled) return
			const currentSize = sizeRef.current
			const currentCount = countRef.current
			const w = container.clientWidth || 1
			const h = container.clientHeight || 1
			const aspect = w / h

			// Bounds = visible frustum at z=0 so particles bounce at screen edges, not off-screen
			const halfFovRad = (CAMERA_FOV_DEG * Math.PI) / 180 / 2
			const halfHeight = CAMERA_Z * Math.tan(halfFovRad)
			const halfWidth = halfHeight * aspect
			let bounds = { x: halfWidth, y: halfHeight }

			const scene = new THREE.Scene()
			scene.background = null
			const camera = new THREE.PerspectiveCamera(CAMERA_FOV_DEG, aspect, 0.1, 1000)
			camera.position.z = CAMERA_Z

			const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
			renderer.setSize(w, h)
			renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
			renderer.setClearColor(0x000000, 0)
			renderer.domElement.style.pointerEvents = 'none'
			container.appendChild(renderer.domElement)

			const texture = makeEmojiTexture(emoji, 256)
			const particles: ParticleData[] = []

			// Base geometry 1x1; scale mesh by size so size prop controls on-screen size
			const baseHalf = 1
			const geo = new THREE.PlaneGeometry(baseHalf * 2, baseHalf * 2)
			for (let i = 0; i < currentCount; i++) {
				const mat = new THREE.MeshBasicMaterial({
					map: texture,
					transparent: true,
					depthWrite: false,
					side: THREE.DoubleSide
				})
				const mesh = new THREE.Mesh(geo, mat)
				mesh.scale.setScalar(currentSize)

				mesh.position.set(
					(Math.random() - 0.5) * bounds.x * 2,
					(Math.random() - 0.5) * bounds.y * 2,
					0
				)

				const angle = Math.random() * Math.PI * 2
				const speed = 1.5 + Math.random() * 2.5

				particles.push({
					mesh,
					radius: baseHalf * currentSize * 0.6,
					vx: Math.cos(angle) * speed,
					vy: Math.sin(angle) * speed,
					vz: 0,
					rotSpeed: (Math.random() - 0.5) * 1.2,
					rotAxis: new THREE.Vector3(0, 0, 1)
				})
				scene.add(mesh)
			}

			const SEPARATION_MARGIN = 0.02

			const resolveCollisions = () => {
				for (let i = 0; i < particles.length; i++) {
					for (let j = i + 1; j < particles.length; j++) {
						const a = particles[i]
						const b = particles[j]
						const dx = b.mesh.position.x - a.mesh.position.x
						const dy = b.mesh.position.y - a.mesh.position.y
						const dist = Math.sqrt(dx * dx + dy * dy)
						const minDist = a.radius + b.radius

						if (dist < minDist && dist > 0.001) {
							const nx = dx / dist
							const ny = dy / dist
							const dvx = a.vx - b.vx
							const dvy = a.vy - b.vy
							const dot = dvx * nx + dvy * ny

							if (dot > 0) {
								a.vx -= dot * nx
								a.vy -= dot * ny
								b.vx += dot * nx
								b.vy += dot * ny
							}

							const overlap = (minDist - dist) / 2 + SEPARATION_MARGIN
							a.mesh.position.x -= nx * overlap
							a.mesh.position.y -= ny * overlap
							b.mesh.position.x += nx * overlap
							b.mesh.position.y += ny * overlap
						}
					}
				}
			}

			const clock = new THREE.Clock()
			const quat = new THREE.Quaternion()
			let animFrameId: number

			const animate = () => {
				animFrameId = requestAnimationFrame(animate)
				const dt = Math.min(clock.getDelta(), 0.05)

				for (const p of particles) {
					p.mesh.position.x += p.vx * dt
					p.mesh.position.y += p.vy * dt
					p.mesh.position.z = 0

					quat.setFromAxisAngle(p.rotAxis, p.rotSpeed * dt)
					p.mesh.quaternion.multiply(quat)

					const px = p.mesh.position.x
					const py = p.mesh.position.y
					if (px > bounds.x || px < -bounds.x) {
						p.vx *= -1
						p.mesh.position.x = Math.sign(px) * bounds.x
					}
					if (py > bounds.y || py < -bounds.y) {
						p.vy *= -1
						p.mesh.position.y = Math.sign(py) * bounds.y
					}
				}

				resolveCollisions()
				renderer.render(scene, camera)
			}

			animate()

			const onResize = () => {
				const c = containerRef.current
				if (!c) return
				const w = c.clientWidth || 1
				const h = c.clientHeight || 1
				const newAspect = w / h
				camera.aspect = newAspect
				camera.updateProjectionMatrix()
				renderer.setSize(w, h)
				const halfFovRad = (CAMERA_FOV_DEG * Math.PI) / 180 / 2
				const halfHeight = CAMERA_Z * Math.tan(halfFovRad)
				bounds = { x: halfHeight * newAspect, y: halfHeight }
			}

			window.addEventListener('resize', onResize)

			cleanup = () => {
				window.removeEventListener('resize', onResize)
				cancelAnimationFrame(animFrameId)
				renderer.dispose()
				texture.dispose()
				geo.dispose()
				particles.forEach((p) => {
					;(p.mesh.material as THREE.Material).dispose()
				})
				if (container.contains(renderer.domElement)) {
					container.removeChild(renderer.domElement)
				}
			}
		})

		return () => {
			cancelled = true
			cancelAnimationFrame(initFrameId)
			cleanup?.()
		}
	}, [emoji, count, size])

	return (
		<Box
			ref={containerRef}
			position='fixed'
			top={0}
			left={0}
			w='100vw'
			h='100vh'
			overflow='hidden'
			pointerEvents='none'
			zIndex={zIndex}
		/>
	)
}
